import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

// ============================================================================
// PageChrome — the contract between a page and the app shell
//
// Most pages are content inside the shell's padded scroll area. A few (the
// GRMS dashboard, the housekeeping board) own the full width because they
// carry their own sticky header strip. Those used to reach out of the shell
// with `-mx-4 -mt-4`, which broke the moment the shell's padding changed and
// left a horizontal scrollbar on narrow viewports. They now *ask* for the
// full-bleed slot instead, and MainLayout drops its own padding for them.
// ============================================================================

interface PageChromeValue {
  /** True while a page has requested the full-bleed slot. */
  fullBleed: boolean;
  setFullBleed: (value: boolean) => void;
}

const PageChromeContext = createContext<PageChromeValue | null>(null);

export function PageChromeProvider({ children }: { children: React.ReactNode }) {
  const [fullBleedCount, setFullBleedCount] = useState(0);

  // A count rather than a boolean: during a route change React can mount the
  // next page before unmounting the previous one, and a boolean would be left
  // false by the outgoing page's cleanup.
  const setFullBleed = useCallback((value: boolean) => {
    setFullBleedCount((count) => Math.max(0, count + (value ? 1 : -1)));
  }, []);

  const value = useMemo<PageChromeValue>(
    () => ({ fullBleed: fullBleedCount > 0, setFullBleed }),
    [fullBleedCount, setFullBleed],
  );

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export function usePageChrome(): PageChromeValue {
  const value = useContext(PageChromeContext);
  if (!value) {
    // Components rendered without the shell have no padding to opt out of.
    return NO_CHROME;
  }
  return value;
}

const NO_CHROME: PageChromeValue = { fullBleed: false, setFullBleed: () => undefined };

/**
 * Claim the shell's full-bleed slot for as long as this page is mounted. Call
 * it instead of adding negative margins: the page then starts at the shell's
 * content edge and can make its own header `sticky top-0`.
 */
export function useFullBleedLayout(): void {
  const { setFullBleed } = usePageChrome();

  // Layout effect, not effect: the padded layout must never paint first.
  useLayoutEffect(() => {
    setFullBleed(true);
    return () => setFullBleed(false);
  }, [setFullBleed]);
}
